/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { AuthInternalError, MiddlewareSteps, TransportMiddlewareProps, isErrorThatNeedToBeShown } from '../../../../core';

import type { NextFunction, TransportMiddlewareContext, TransportMiddlewareRequest, TransportMiddlewareResponse } from '../../../../core';
import type { FileTransportCommands, StreamProgressData } from '../../../../services/file-transport/interfaces';

const isSuccessUploadsExists = (responses: any[]): boolean => responses.some(resp => resp === true);

export const handleStreamUploadRestErrorMiddleware = <T, R, E>(
    next: NextFunction<TransportMiddlewareResponse<R, E> | Error>,
    ctx: TransportMiddlewareContext,
    req: TransportMiddlewareRequest<T>,
    res: TransportMiddlewareResponse<R, E>
) => {
    const { commands, middlewareConfigs } = ctx;
    const { url, options, streamData, requestInit } = req;
    const { response } = res;
    const { handleStreamUploadErrorMiddleware } = middlewareConfigs;
    const { notShowDefaultError, backendErrorTitle, type } = options;
    const errorResponse = res[TransportMiddlewareProps.ErrorResponse];

    if (errorResponse instanceof AuthInternalError) {
        commands.showAuthError();
        next(res);
        return;
    }

    if (response.ok) {
        // case when last from multiple files finished successfully, but previous files have errors
        if (!notShowDefaultError) {
            (commands as FileTransportCommands).showStreamBackendError(res[TransportMiddlewareProps.ErrorResponse] as unknown as any[], {
                url,
                title: backendErrorTitle ?? null,
                type,
            });
        }

        res[TransportMiddlewareProps.ErrorResponse] = isSuccessUploadsExists(res[TransportMiddlewareProps.ResponseProp] as unknown as any[])
            ? res[TransportMiddlewareProps.ResponseProp]
            : res[TransportMiddlewareProps.ErrorResponse];

        next(res);
        return;
    }

    if (requestInit.signal?.aborted) {
        return response.json()
            .then((errorResponse: E) => {
                res[TransportMiddlewareProps.ErrorResponse] = errorResponse as E;
                next(res);
            });
    }

    const { fileNumber, totalFiles, currentPieceSendAttempts } = streamData as StreamProgressData;

    if (currentPieceSendAttempts < (handleStreamUploadErrorMiddleware.resendPieceAttempts ?? 2)) {
        // case for retry send current chunk or file
        streamData.currentPieceSendAttempts += 1;
        req.rerunConfig.setStep(MiddlewareSteps.Run);
        next(res);
        return;
    }

    if (!res[TransportMiddlewareProps.ErrorResponse]) {
        res[TransportMiddlewareProps.ErrorResponse] = [] as unknown as E;
    }

    response.json()
        .then((errorResponse: E) => {
            commands.log(url, errorResponse);

            if (fileNumber < totalFiles) {
                // multiple files and current finished with error
                streamData.currentPieceSendAttempts = 1;
                streamData.fileNumber += 1;
                req.rerunConfig.setStep(MiddlewareSteps.Run);

                (res[TransportMiddlewareProps.ResponseProp] as unknown as any[]).push(errorResponse); // errors push both responses
                res[TransportMiddlewareProps.ErrorResponse].push(errorResponse);
                next(res);
                return;
            }

            // last file among multiple finished with error
            (res[TransportMiddlewareProps.ResponseProp] as unknown as any[]).push(errorResponse); // errors push both responses
            res[TransportMiddlewareProps.ErrorResponse].push(errorResponse);

            if (!notShowDefaultError) {
                (commands as FileTransportCommands).showStreamBackendError(res[TransportMiddlewareProps.ErrorResponse] as unknown as any[], {
                    url,
                    title: backendErrorTitle ?? null,
                    type,
                });
            }

            next(res);
        })
        .catch((err: Error) => {
            commands.log(url, response as Response);
            isErrorThatNeedToBeShown(err) && console.error(err);

            if (fileNumber < totalFiles) {
                // multiple files and current finished with error
                streamData.currentPieceSendAttempts = 1;
                streamData.fileNumber += 1;
                req.rerunConfig.setStep(MiddlewareSteps.Run);

                (res[TransportMiddlewareProps.ResponseProp] as unknown as any[]).push(err);
                res[TransportMiddlewareProps.ErrorResponse].push(err);
                next(res);
                return;
            }

            // last file among multiple finished with error
            (res[TransportMiddlewareProps.ResponseProp] as unknown as any[]).push(err);
            res[TransportMiddlewareProps.ErrorResponse].push(err);

            if (!notShowDefaultError) {
                (commands as FileTransportCommands).showStreamBackendError(res[TransportMiddlewareProps.ErrorResponse] as unknown as any[], {
                    url,
                    title: backendErrorTitle ?? null,
                    type,
                });
            }

            res[TransportMiddlewareProps.ErrorResponse] = isSuccessUploadsExists(res[TransportMiddlewareProps.ResponseProp] as unknown as any[])
                ? res[TransportMiddlewareProps.ResponseProp]
                : res[TransportMiddlewareProps.ErrorResponse];

            next(res);
        });
};
